import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Text, UnstyledButton } from '@mantine/core';
import { TalkingHeadActorEventIdn } from '@newo/api-client';
import { PhoneController } from '@newo/ui';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';
import PhoneInput from '@/components/PhoneInput';
import Title from '@/components/Title';
import { useHostContext } from '@/context/HostContext.tsx';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app.ts';
import { AgentTrainingStep } from '@/types/agent-training.ts';
import { cn } from '@/utils';
import { useActorSendEventMutation } from '@/utils/api.ts';
import { createContactAgreementSchema } from '@/utils/validation.ts';

type TalkToAiAgentPayload = {
	phone_number: string;
};
const TalkToAiAgent: FC = () => {
	const { mutate: sendActorEvent } = useActorSendEventMutation();
	const customerData = useAppStore((state) => state.customerData);

	const { setTrainingStep } = useAgentTraining();
	const { isMiniCreatorMode, isOverlayMode, localization } = useHostContext();

	const { clientSecret, externalId, connectorIdn, agentData } = useAppStore(
		useShallow((state) => ({
			agentData: state.agentData,
			clientSecret: state.clientSecret,
			externalId: state.externalId,
			connectorIdn: state.connectorIdn,
			setAppState: state.setAppState
		}))
	);

	const schema = useMemo(
		() => createContactAgreementSchema(localization.translate),
		[localization.translate]
	);

	const {
		handleSubmit,
		control,
		setValue,
		formState: { isValid }
	} = useForm<TalkToAiAgentPayload>({
		resolver: zodResolver(schema),
		defaultValues: {
			phone_number: customerData?.contact_phone
		}
	});

	useEffect(() => {
		if (customerData?.contact_phone) {
			setValue('phone_number', customerData?.contact_phone, {
				shouldDirty: true,
				shouldTouch: true
			});
		}
	}, [customerData, setValue]);

	const onFormSubmit = (data: TalkToAiAgentPayload) => {
		if (!clientSecret || !connectorIdn || !externalId) {
			return;
		}

		sendActorEvent({
			client_secret: clientSecret,
			connector_idn: connectorIdn,
			user_actor_id: agentData?.user_actor_id,
			event_idn: TalkingHeadActorEventIdn.ContactConsentReceived,
			external_id: externalId,
			arguments: [
				{
					name: 'phone_number',
					value: data.phone_number ?? ''
				}
			]
		});

		setTrainingStep(AgentTrainingStep.Loading);
	};

	const handleBack = () => {
		setTrainingStep(AgentTrainingStep.Loading);
	};
	return (
		<div className="h-full">
			<div className="animate-fadeIn h-full">
				<form
					className="flex flex-col gap-[35px] justify-between h-full w-full"
					onSubmit={handleSubmit(onFormSubmit)}
				>
					<div className="w-full flex flex-col h-full justify-between pb-[35px]">
						<Title
							className={cn(
								'intro-title w-full  text-primary-900 text-left [font-family:var(--font-headlines)] font-semibold m-0',
								{
									'!text-[24px] sm:!text-[32px]': !isMiniCreatorMode,
									'!text-sm opacity-90 !leading-tight': isMiniCreatorMode,
									'!text-lg !leading-tight': isOverlayMode
								}
							)}
							level={4}
							size="h5"
						>
							{localization.translate('creator', 'contact_form.title')}
						</Title>

						<PhoneController
							control={control}
							name="phone_number"
							render={(props) => (
								<PhoneInput
									{...props}
									className="col-end-2 mt-[35px] md:mt-[auto]"
								/>
							)}
							revalidateOn={localization.language}
						/>
					</div>

					<div className="flex w-full justify-between gap-3">
						<UnstyledButton
							className="text-[#495057] ![font-family:var(--font-body)] text-[14px] font-normal leading-[21px] not-italic"
							onClick={handleBack}
						>
							{localization.translate('common', 'skip')}
						</UnstyledButton>

						<div className="flex gap-[30px] h-full relative items-center">
							<Text className="![font-family:var(--font-body)] !text-[13px] !font-normal hidden sm:block">
								{localization.translate(
									'creator',
									'contact_form.agree_to_be_contacted'
								)}
							</Text>

							<Button
								className={cn('!h-10 !text-sm !font-medium sm:max-w-fit', {
									'!opacity-50': !isValid
								})}
								disabled={!isValid}
								type="submit"
							>
								{localization.translate('common', 'agree')}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default TalkToAiAgent;
